import React from 'react';
import styled from 'styled-components';
import { IAthlete, ITeam, IUser } from '../../../../data/types';
import { TAB_STATE } from './tab_state';

export interface IErrorState {
  active: boolean;
  message?: string;
  items?: Array<Partial<IAthlete>>;
}

export interface IDuplicateLookup {
  uniqueId: Map<string, string>;
  name: Map<string, string>;
}

export interface IErrorLookup {
  [inputName: string]: {
    hasError: boolean;
    message: string;
  };
}

/**
 * @description hook to compute errors
 * @param athlete
 * @returns
 */
export const useErrorLookup = (
  athlete: Partial<IAthlete>,
  duplicateLookup: IDuplicateLookup
): IErrorLookup => {
  return React.useMemo((): IErrorLookup => {
    const nameMatch = duplicateLookup.name.get(athlete?.name as string);
    const uniqueIdMatch = duplicateLookup.uniqueId.get(
      athlete?.external?.uniqueId as string
    );
    return {
      name: {
        hasError: isDuplicate(nameMatch, athlete?.id) ?? false,
        message: 'An athlete with the same name already exists',
      },
      uniqueId: {
        hasError: isDuplicate(uniqueIdMatch, athlete?.id) ?? false,
        message: 'An athlete with the same unique id already exists',
      },
    };
  }, [
    athlete?.name,
    athlete?.external?.uniqueId,
    JSON.stringify(duplicateLookup),
  ]);
};

/**
 * @description determines if the value belongs to a different athlete
 * @param value
 * @param athleteId
 * @returns
 */
const isDuplicate = (
  value: string | undefined,
  athleteId: string | undefined
): boolean => {
  if (value) {
    if (athleteId) {
      return value !== athleteId;
    } else {
      return true;
    }
  }
  return false;
};

/**
 * @description checks for duplicate value errors
 */

export const useDuplicateLookup = (athletes: IAthlete[]): IDuplicateLookup => {
  return React.useMemo((): IDuplicateLookup => {
    const names: Map<string, string> = new Map();
    const uniqueIds: Map<string, string> = new Map();

    for (const item of athletes) {
      names.set(item.name, item.id);
      if (item?.external?.uniqueId) {
        uniqueIds.set(item?.external?.uniqueId?.toString(), item.id);
      }
    }

    return {
      name: names,
      uniqueId: uniqueIds,
    };
  }, [JSON.stringify(athletes)]);
};

export interface IUseFormErrorDeps {
  activeTab?: TAB_STATE;
  athlete?: Partial<IAthlete>;
  selectedTeams: ITeam[];
  importList?: Array<Partial<IAthlete>>;
  user: IUser;
}
export const useFormError = (deps: IUseFormErrorDeps): boolean => {
  const { activeTab, athlete, selectedTeams, importList, user } = deps;
  return React.useMemo((): boolean => {
    /// if importing list, validation is handled on server only
    if (activeTab !== undefined && activeTab === TAB_STATE.IMPORT) {
      return importList?.length === 0 || selectedTeams?.length === 0;
    } else {
      if (!athlete?.name) return true;
      if (athlete?.name?.length === 0) return true;
      if (selectedTeams?.length === 0) return true;
      if (
        user?.orgOptions?.enforceUniqueAthleteId === true &&
        (athlete.external?.uniqueId === undefined ||
          athlete.external?.uniqueId?.length === 0)
      ) {
        return true;
      }

      return false;
    }
  }, [
    athlete.name,
    athlete?.external?.uniqueId,
    selectedTeams.length,
    activeTab,
    importList?.length,
    JSON.stringify(user),
  ]);
};

export const ErrorBanner = (props: IErrorBannerProps): React.ReactElement => (
  <ErrorBannerContainer>
    <h5>An Error Occured. {props.message}</h5>
  </ErrorBannerContainer>
);

interface IErrorBannerProps {
  message?: string;
}

const ErrorBannerContainer = styled.div`
  height: 50px;
  width: 100%;
  background-color: red;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;
